/**
 * Age verification gate. Blocks the whole site on first visit until the
 * visitor confirms they meet the minimum age requirement. Choice is stored
 * in localStorage so repeat visitors are never re-prompted.
 */
import React, { useEffect, useState } from "react";
import { ShieldAlert } from "lucide-react";

const KEY = "endag1_age_verified_v1";
const MIN_AGE = 21;

export default function AgeGate() {
  const [ready, setReady] = useState(false);
  const [confirmed, setConfirmed] = useState(true); // assume verified until we check

  useEffect(() => {
    let ok = false;
    try { ok = localStorage.getItem(KEY) === "yes"; } catch { /* private mode */ }
    setConfirmed(ok);
    setReady(true);
    // Lock scroll while the modal is up
    if (!ok) document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const confirm = () => {
    try { localStorage.setItem(KEY, "yes"); } catch { /* ignore */ }
    document.body.style.overflow = "";
    setConfirmed(true);
  };

  const decline = () => {
    // Send them away — Google is a safe universal fallback
    window.location.href = "https://www.google.com";
  };

  if (!ready || confirmed) return null;

  return (
    <div
      data-testid="age-gate"
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-gate-title"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#0F0F10]/95 backdrop-blur-md" />

      {/* Card */}
      <div className="relative w-full max-w-lg border border-white/10 bg-[#171717] p-8 md:p-10 shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-full bg-[#4ADE80]/15 flex items-center justify-center">
            <ShieldAlert className="h-5 w-5 text-[#4ADE80]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#4ADE80]">Age Verification</div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#A8A8A8]">Research Use Only</div>
          </div>
        </div>

        <h2 id="age-gate-title" className="font-display text-3xl md:text-4xl text-[#F5F5F5] leading-tight">
          Are you {MIN_AGE} or older?
        </h2>

        <p className="mt-5 text-sm text-[#A8A8A8] leading-relaxed">
          EndaG1 supplies pharmaceutical-grade research compounds intended strictly for
          laboratory and <em>in-vitro</em> research use. Products are <strong className="text-[#F5F5F5]">not for human
          or veterinary use</strong>. By entering this site you confirm that you are at least {MIN_AGE} years old
          and understand the intended use.
        </p>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            data-testid="age-gate-confirm"
            onClick={confirm}
            className="btn-press bg-[#4ADE80] text-[#0F0F10] font-semibold px-6 py-3.5 text-[12px] uppercase tracking-[0.16em] hover:bg-[#6FE998]"
          >
            I am {MIN_AGE} or older — enter
          </button>
          <button
            data-testid="age-gate-decline"
            onClick={decline}
            className="btn-press border border-white/20 text-[#A8A8A8] font-semibold px-6 py-3.5 text-[12px] uppercase tracking-[0.16em] hover:border-white/40 hover:text-[#F5F5F5]"
          >
            I am under {MIN_AGE} — leave
          </button>
        </div>

        <div className="mt-6 pt-5 border-t border-white/10 text-[10px] text-[#7a7a7a] leading-relaxed">
          By continuing you also accept our{" "}
          <a href="/terms" className="underline hover:text-[#F5F5F5]">Terms</a>,{" "}
          <a href="/disclaimer" className="underline hover:text-[#F5F5F5]">Disclaimer</a> and{" "}
          <a href="/privacy" className="underline hover:text-[#F5F5F5]">Privacy</a>.
        </div>
      </div>
    </div>
  );
}
